import { type Database } from "@fitness-app/supabase";

// TRE-1590 — per-trainee goals (water / steps / weight), one row per (trainee, type).
// Goals feed dashboards and statistics ONLY — challenge scoring keeps its own numericGoal
// targets. Water and steps are client-editable daily targets; weight is a trainer-set,
// three-threshold gamification goal whose tiers award badges (SQL trigger, see the
// 20260716120000 migration).

export type ClientGoalEntity = Database["public"]["Tables"]["client_goal"]["Row"];

export enum ClientGoalType {
  Water = "water",
  Steps = "steps",
  Weight = "weight",
}

export enum ClientGoalUpdatedBy {
  Trainer = "trainer",
  Client = "client",
}

export enum WeightGoalDirection {
  Loss = "loss",
  Gain = "gain",
}

/** Ordered bronze → gold; the 1-based position doubles as the badge tier. */
export enum WeightGoalTier {
  Bronze = "bronze",
  Silver = "silver",
  Gold = "gold",
}

export interface WeightGoalThreshold {
  tier: WeightGoalTier;
  value: number;
}

/** Typed view over client_goal.config (shape depends on type). */
export type ClientGoalConfig = {
  /** water: daily target in ml. */
  targetMl?: number;
  /** steps: daily target. */
  targetSteps?: number;
  /** weight: whether thresholds are reached by losing or gaining. */
  direction?: WeightGoalDirection;
  /** weight: thresholds ordered bronze → gold, monotonic per direction. */
  thresholds?: WeightGoalThreshold[];
  /** weight: the client's weight when the goal was set — the progress baseline for tier 1. */
  startWeight?: number;
} & Record<string, unknown>;

export interface ClientGoal extends Omit<ClientGoalEntity, "type" | "config" | "updatedBy"> {
  type: ClientGoalType;
  config: ClientGoalConfig;
  updatedBy: ClientGoalUpdatedBy;
}

export const WEIGHT_GOAL_TIERS: WeightGoalTier[] = [WeightGoalTier.Bronze, WeightGoalTier.Silver, WeightGoalTier.Gold];

export const asClientGoalConfig = (raw: unknown): ClientGoalConfig =>
  raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as ClientGoalConfig) : {};

/**
 * Sanitized thresholds normalized to canonical bronze → gold order (drops malformed entries and
 * duplicate tiers — first occurrence wins). Positions are meaningful: index + 1 = badge tier.
 */
export const weightGoalThresholds = (config: ClientGoalConfig): WeightGoalThreshold[] => {
  const valid = (Array.isArray(config.thresholds) ? config.thresholds : []).filter(
    (threshold): threshold is WeightGoalThreshold =>
      !!threshold &&
      typeof threshold === "object" &&
      WEIGHT_GOAL_TIERS.includes((threshold as WeightGoalThreshold).tier) &&
      typeof (threshold as WeightGoalThreshold).value === "number" &&
      Number.isFinite((threshold as WeightGoalThreshold).value) &&
      (threshold as WeightGoalThreshold).value > 0,
  );

  return WEIGHT_GOAL_TIERS.flatMap((tier) => {
    const match = valid.find((threshold) => threshold.tier === tier);
    return match ? [match] : [];
  });
};

/**
 * Thresholds already reached by a weight value (permanent semantics — callers should treat a
 * once-reached tier as kept even if the current weight regresses; the badge layer guarantees it).
 */
export const reachedWeightThresholds = (config: ClientGoalConfig, weight: number): WeightGoalThreshold[] =>
  weightGoalThresholds(config).filter((threshold) =>
    config.direction === WeightGoalDirection.Gain ? weight >= threshold.value : weight <= threshold.value,
  );
